import { useEffect } from "react";
import { api } from "../lib/api";

interface Props {
  /** Raw stored URLs (same as referenceImageUrls). */
  urls: string[];
  index: number;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}

export function ImageLightbox({ urls, index, onClose, onIndexChange }: Props) {
  const safeIndex = urls.length ? ((index % urls.length) + urls.length) % urls.length : 0;
  const src = urls[safeIndex] ? api.mediaUrl(urls[safeIndex]) : "";
  const multi = urls.length > 1;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopImmediatePropagation();
        onClose();
        return;
      }
      if (!multi) return;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        e.stopImmediatePropagation();
        onIndexChange((safeIndex + 1) % urls.length);
      }
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        e.stopImmediatePropagation();
        onIndexChange((safeIndex - 1 + urls.length) % urls.length);
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [multi, onClose, onIndexChange, safeIndex, urls.length]);

  if (!urls.length || !src) return null;

  return (
    <div
      className="lightbox-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
    >
      <button
        type="button"
        className="lightbox-close"
        onClick={onClose}
        aria-label="Close"
      >
        ×
      </button>

      {multi && (
        <button
          type="button"
          className="lightbox-nav lightbox-prev"
          onClick={(e) => {
            e.stopPropagation();
            onIndexChange((safeIndex - 1 + urls.length) % urls.length);
          }}
          aria-label="Previous image"
        >
          ‹
        </button>
      )}

      <div className="lightbox-stage" onClick={(e) => e.stopPropagation()}>
        <img src={src} alt="" className="lightbox-img" />
        {multi && (
          <div className="lightbox-counter">
            {safeIndex + 1} / {urls.length}
          </div>
        )}
      </div>

      {multi && (
        <button
          type="button"
          className="lightbox-nav lightbox-next"
          onClick={(e) => {
            e.stopPropagation();
            onIndexChange((safeIndex + 1) % urls.length);
          }}
          aria-label="Next image"
        >
          ›
        </button>
      )}
    </div>
  );
}
